// Renders one Audio Description event through the active TTS backend
// (embedding parent host when licensed, otherwise the cloud function) and
// returns the result without touching the event, so callers working on
// cloned events (batch render) and live events (event editor) can share it.

import { functions } from "@app/configs/firebase.js";
import { httpsCallable } from "firebase/functions";
import { environment } from "@app/store/envStore.svelte.js";
import convertToPlainText from "@cclib/functions/quill/convertToPlainText.js";
import tcLib from "@cclib/lib/timecode.js";
import voiceUtils from "@cclib/providers/textToSpeechAll/voiceUtils.js";
import {
    renderParentAudio,
    getAudioDurationFromUrl,
    MAX_TTS_CHARS,
} from "@app/lib/audio/parentTts.js";

/**
 * Plain text for an event as it should be sent to its voice. Emotion tags
 * such as [laughs] are kept for expressive models and stripped otherwise.
 *
 * @param {object} event
 * @returns {string}
 */
export function getEventTtsText(event) {
    const plainText = convertToPlainText(event?.text || "", " ");
    return voiceUtils.prepareTtsText(plainText, event?.voice, event?.speakingStyle);
}

/**
 * @param {object} event
 * @param {string} eventGroupId
 * @returns {Promise<{ eventId: string, audio: string, durationSec: number, storagePath?: string } | null>} null when the event has no speakable text
 */
export async function renderEventTts(event, eventGroupId) {
    const text = getEventTtsText(event);
    if (!text) return null;

    if (environment.adLicense) {
        if (text.length > MAX_TTS_CHARS) {
            throw new Error(`Text exceeds the ${MAX_TTS_CHARS} character limit for local text-to-speech.`);
        }

        const result = await renderParentAudio({
            text,
            voice: event.voice.id,
            speed: 1,
            format: "mp3",
            eventId: event.id,
        });

        if (!result || result.success === false || !result.url) {
            throw new Error(result?.message || "No audio returned from local text-to-speech.");
        }

        return {
            eventId: result.eventId || event.id,
            audio: result.url,
            durationSec: await getAudioDurationFromUrl(result.url),
        };
    }

    const res = await httpsCallable(functions, "v8TextToSpeechProviderV5")({
        provider: event.voice.provider,
        language: event.voice.language,
        voice: event.voice.name,
        voice_id: event.voice.id,
        engine: event.voice.engine,
        model: voiceUtils.renderModel(event.voice, event.speakingStyle),
        stability: event.voiceStability / 100,
        similarity: event.voiceSimilarity / 100,
        speed: 1,
        style: voiceUtils.renderStyle(event.voice, event.speakingStyle),
        audioEncoding: "MP3",
        text,
        eventGroupId,
        eventId: event.id,
    });

    if (!res?.data) {
        throw new Error("No response from text-to-speech service.");
    }

    const duration = res.data.duration;
        return {
            eventId: res.data.eventId || event.id,
            audio: res.data.audio,
            durationSec: typeof duration === "number" ? duration / 1000 : tcLib.tcMsToSec(duration),
            storagePath: res.data.storagePath || "",
        };
}
